import { type Table as ReactTable, flexRender } from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Trash2, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DataTableProps<TData> {
  table: ReactTable<TData>
  isLoading?: boolean
  onRowClick?: (row: TData) => void
  onBulkDelete?: (ids: string[]) => void
  onBulkRetry?: (ids: string[]) => void
  getRowId?: (row: TData) => string
}

export function DataTable<TData>({
  table,
  isLoading,
  onRowClick,
  onBulkDelete,
  onBulkRetry,
  getRowId = (row: TData) => (row as { fileId: string }).fileId,
}: DataTableProps<TData>) {
  const selectedRows = table.getFilteredSelectedRowModel().rows
  const hasSelection = selectedRows.length > 0

  return (
    <div className="space-y-4">
      {/* Bulk action bar - shows when rows selected */}
      {hasSelection && (onBulkDelete || onBulkRetry) && (
        <div className="flex items-center justify-between rounded-md border bg-muted p-4">
          <p className="text-sm text-muted-foreground">
            {selectedRows.length} of {table.getFilteredRowModel().rows.length} row(s) selected
          </p>
          <div className="flex gap-2">
            {onBulkRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const ids = selectedRows.map((row) => getRowId(row.original))
                  onBulkRetry(ids)
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry Selected
              </Button>
            )}
            {onBulkDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  const ids = selectedRows.map((row) => getRowId(row.original))
                  onBulkDelete(ids)
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Selected
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    onRowClick && 'cursor-pointer hover:bg-muted/50'
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
